"use client";

import { useCallback } from "react";
import { loginAction } from "@/actions/serverActions/auth/loginAction";
import { logoutAction } from "@/actions/serverActions/auth/logoutAction";
import { refreshSessionAction } from "@/actions/serverActions/auth/refreshSessionAction";
import { requestPasswordResetAction } from "@/actions/serverActions/auth/requestPasswordResetAction";
import { resetPasswordWithOtpAction } from "@/actions/serverActions/auth/resetPasswordWithOtpAction";
import { verifyResetOtpAction } from "@/actions/serverActions/auth/verifyResetOtpAction";
import { getFullPath, LOCAL_STORE_NAME_CONFIG } from "@/config";
import { clearCsrfTokenCache } from "@/lib/csrf-client";
import { useAlert } from "@/Provider/AlertProvider/AlertProvider";
import {
  clearAuthForm,
  setIsLoading,
} from "@/store/Features/states/auth-management/authSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useServerAction } from "./useServerAction";

/**
 * Auth hook — wires React UI to all auth server actions.
 *
 * `email`, `password`, and `isLoading` live in Redux (`state.auth`) so the
 * form fields are controlled and survive re-renders. Tokens never reach the
 * client — only the encrypted Redis session does.
 */
export function useAuth() {
  const dispatch = useAppDispatch();
  const { email, password } = useAppSelector((state) => state.auth);
  const { showError, showSuccess } = useAlert();

  // ── Bind every action ONCE; CSRF auto-injected by useServerAction ──────
  const login = useServerAction(loginAction);
  const logout = useServerAction(logoutAction);
  const refreshSession = useServerAction(refreshSessionAction);
  const requestPasswordReset = useServerAction(requestPasswordResetAction);
  const verifyResetOtp = useServerAction(verifyResetOtpAction);
  const resetPassword = useServerAction(resetPasswordWithOtpAction);

  /**
   * Submit login credentials. When `remember` is true, persist the email in
   * localStorage for the next visit; otherwise clear it. Session tokens stay
   * server-side in Redis — only the email is stored client-side.
   */
  const handleLogin = useCallback(
    async (remember?: boolean) => {
      dispatch(setIsLoading(true));
      try {
        // Trim stray leading/trailing whitespace (paste/autofill often appends a
        // space) so it can't cause a spurious 401 from the backend.
        const trimmedEmail = email.trim();
        const result = await login({
          email: trimmedEmail,
          password: password.trim(),
        });
        if (result.success) {
          try {
            if (remember) {
              localStorage.setItem(
                LOCAL_STORE_NAME_CONFIG.REMEMBER_EMAIL,
                trimmedEmail,
              );
            } else {
              localStorage.removeItem(LOCAL_STORE_NAME_CONFIG.REMEMBER_EMAIL);
            }
          } catch {}
          showSuccess(result.message ?? "Login successful");
          window.location.href = getFullPath("/dashboard");
        } else {
          showError(result.message ?? "Login failed");
        }
        return result;
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [login, email, password, dispatch, showError, showSuccess],
  );

  // ── Logout ─────────────────────────────────────────────────────────────
  const handleLogout = useCallback(async () => {
    dispatch(setIsLoading(true));
    try {
      await logout();
      clearCsrfTokenCache();
      dispatch(clearAuthForm());
      window.location.href = getFullPath("/auth/login");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Logout failed";
      showError(message);
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [logout, dispatch, showError]);

  // ── Refresh session (idle-timeout extender) ────────────────────────────
  const handleRefreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const result = await refreshSession();
      return result.success;
    } catch {
      return false;
    }
  }, [refreshSession]);

  // ── Password reset: step 1 — request OTP ───────────────────────────────
  const requestPasswordResetHandler = useCallback(
    async (emailArg: string) => {
      const result = await requestPasswordReset({ email: emailArg });
      return result;
    },
    [requestPasswordReset],
  );

  // ── Password reset: step 2 — verify OTP ────────────────────────────────
  const verifyResetOtpHandler = useCallback(
    async (emailArg: string, otp: string) => {
      const result = await verifyResetOtp({ email: emailArg, otp });
      return result;
    },
    [verifyResetOtp],
  );

  // ── Password reset: step 3 — set new password ──────────────────────────
  const resetPasswordWithOtpHandler = useCallback(
    async (emailArg: string, otp: string, newPassword: string) => {
      const result = await resetPassword({
        email: emailArg,
        otp,
        newPassword,
      });
      if (result.success) {
        showSuccess(result.message ?? "Password reset");
      } else {
        showError(result.message ?? "Password reset failed");
      }
      return result;
    },
    [resetPassword, showError, showSuccess],
  );

  return {
    // Form state from Redux
    email,
    password,
    dispatch,

    // Login / logout
    handleLogin,
    handleLogout,
    handleRefreshSession,

    // Password reset (CRM-compatible names)
    requestPasswordReset: requestPasswordResetHandler,
    verifyResetOtp: verifyResetOtpHandler,
    resetPasswordWithOtp: resetPasswordWithOtpHandler,
  };
}
